// How each victory track's number is computed and how it reads on screen. Three kinds:
//
//   rungs   space                     rungs of the 9-rung ladder achieved      "7/9"
//   share   tech, economic, culture   weighted mean of shares of world totals  "24.1%"
//   index   diplomatic, score         weighted mean of 0..1 parts, not a share "42.1"
//
// The formulas below mirror src/world/victory.ts (which the UI cannot import); the numbers
// themselves always come from world.victory().

import { yearOf } from '../core/dates.ts';
import type { DateStr } from '../core/schema.ts';
import type { VictoryEntry, VictoryTrack } from '../world/api.ts';
import { fmtShare } from './format.ts';

/** Rungs on the space ladder (milestones.json); world.spaceLadder() reports the same total. */
export const LADDER_RUNGS = 9;

const INDEX_SCALE = 100;

type TrackKind = 'rungs' | 'share' | 'index';

const KIND: Record<VictoryTrack, TrackKind> = {
  space: 'rungs',
  tech: 'share',
  economic: 'share',
  diplomatic: 'index',
  culture: 'share',
  score: 'index',
};

/** What a track measures, in words: the caption of its card. */
export const TRACK_RULE: Record<VictoryTrack, { formula: string; note: string }> = {
  space: {
    formula: 'Rungs achieved of 9 (not a share)',
    note: 'Each rung is a milestone on the space ladder, counted once the state has reached it. It is not a share of a world total.',
  },
  tech: {
    formula: '0.4 AI models + 0.3 TOP500 + 0.3 papers · each a share of the world total',
    note: 'AI models over the two latest years, TOP500 Rmax of the latest list, papers of the latest year. Each is the state\'s share of the world total.',
  },
  economic: {
    formula: '0.4 GDP PPP + 0.4 GDP nominal + 0.2 exports · each a share of the world total',
    note: 'Each input is the state\'s share of the world total at its latest year.',
  },
  diplomatic: {
    formula: 'Index 0–100 (not a share): 0.4 UNGA bloc + 0.3 posts + 0.3 allies · weights rescale over the inputs with data',
    note: 'UNGA bloc: the share of other states voting nearer to this state than to any leader state. Posts and allies: the state\'s count divided by the largest count. Posts (Lowy) have no data before 2016; until then the weights are 0.4/0.7 and 0.3/0.7. Not a share of a world total.',
  },
  culture: {
    formula: '0.5 tourists + 0.3 World Heritage + 0.2 medals · each a share of the world total',
    note: 'Tourist arrivals, World Heritage sites, and Olympic medals of the two latest Games. Each is the state\'s share of the world total.',
  },
  score: {
    formula: 'Your weighted mean of the five tracks, each 0–1 (not a share)',
    note: 'Tracks with weight 0 are left out. Space counts as rungs ÷ 9 and Diplomatic as its 0–1 index, so this is not a share of anything.',
  },
};

/** Whole rungs behind a space progress value (rungs ÷ 9). */
export function rungsOf(progress: number): number {
  return Math.round(progress * LADDER_RUNGS);
}

/** A track's progress value as the track states it: '7/9', '24.1%' or '42.1'. */
export function fmtTrack(track: VictoryTrack, progress: number): string {
  const kind = KIND[track];
  if (kind === 'rungs') {
    return `${rungsOf(progress)}/${LADDER_RUNGS}`;
  }
  if (kind === 'share') {
    return fmtShare(progress);
  }
  return (progress * INDEX_SCALE).toFixed(1);
}

/** Zero in the track's own unit, for chart axes. */
export function trackZero(track: VictoryTrack): string {
  return KIND[track] === 'share' ? '0%' : '0';
}

/**
 * The place of `entries[index]` (best first): one more than the states strictly ahead of it, so
 * equal progress shares a place and the next place is skipped (6/9 twice: #2, #2, then #4).
 *
 *   CHN 7/9   RUS 6/9   USA 6/9   FRA 4/9      →  1, 2=, 2=, 4
 */
export function rankAt(entries: VictoryEntry[], index: number): { pos: number; tied: boolean } {
  const progress = entries[index].progress;
  const ahead = entries.findIndex((e) => e.progress === progress);
  const tied = entries.some((e, i) => i !== index && e.progress === progress);
  return { pos: ahead + 1, tied };
}

/**
 * Bar length, 0..1. Rungs and index points sit on a fixed 0..1 scale; shares are drawn
 * relative to the leading bar, since a share of a world total rarely fills the scale.
 */
export function barFill(track: VictoryTrack, progress: number, max: number): number {
  if (KIND[track] !== 'share') {
    return progress;
  }
  return max > 0 ? progress / max : 0;
}

/**
 * A history chart's points as of a viewed date. The service's yearly points are year-end
 * values, so at 20 Mar 2014 the 2014 point is still in the future: it is replaced by the
 * values at the viewed date and later years are dropped.
 */
export function pastSeries(
  series: Array<{ year: number; entries: VictoryEntry[] }>,
  date: DateStr,
  now: VictoryEntry[],
): Array<{ year: number; entries: VictoryEntry[] }> {
  const year = yearOf(date);
  const past = series.filter((p) => p.year < year);
  return [...past, { year, entries: now }];
}
